import { MessageSquare, Send } from "lucide-react";
import React, { useEffect, useState } from "react";
import api from "~/services/api";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

export default function Comments({ enrollment_id }) {
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [memo, setMemo] = useState("");

  async function getMemos() {
    if (!enrollment_id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/enrollments/${enrollment_id}/memos`);
      setMemos(data);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getMemos();
  }, [enrollment_id]);

  async function handleSubmit() {
    if (!memo.trim()) {
      toast("Please enter a comment.", { type: "warning", autoClose: 2000 });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/enrollments/${enrollment_id}/memos`, {
        memo: memo.trim(),
      });
      setMemos((prev) => [data, ...prev]);
      setMemo("");
      toast("Comment added!", { autoClose: 1500 });
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-4 w-full flex flex-col gap-4">
      <div className="flex flex-col gap-3 border border-gray-200 rounded-lg p-4 bg-slate-50">
        <textarea
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="Type your comment here..."
          rows={3}
          className="w-full border border-gray-300 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-mila_orange"
        />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex flex-row items-center gap-1 bg-mila_orange text-white text-xs rounded-md px-3 py-2 h-8 disabled:opacity-50"
          >
            <Send size={14} /> {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <MessageSquare size={16} className="text-mila_orange" /> Comments ({memos.length})
        </h3>

        {loading ? (
          <p className="text-xs text-gray-400">Loading...</p>
        ) : memos.length === 0 ? (
          <p className="text-xs text-gray-400 py-4 text-center">No comments yet.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {memos.map((item) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-3 bg-white flex flex-col gap-1"
              >
                <div className="flex flex-row items-center justify-between gap-2">
                  <span className="text-xs text-gray-400">
                    {item.created_at
                      ? format(parseISO(item.created_at), "MM/dd/yyyy 'at' HH:mm")
                      : ""}
                  </span>
                  {item.createdBy && (
                    <span className="text-xs text-gray-400">
                      By {item.createdBy.name}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700">{item.memo}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
